import { argocdTranslationRef } from '@backstage-community/plugin-argocd';
import { coreComponentsTranslationRef } from '@backstage/core-components';
import { createTranslationMessages } from '@backstage/frontend-plugin-api';
import { apiDocsTranslationRef } from '@backstage/plugin-api-docs';
import { TranslationBlueprint } from '@backstage/plugin-app-react';
import { catalogTranslationRef } from '@backstage/plugin-catalog';
import { catalogReactTranslationRef } from '@backstage/plugin-catalog-react';
import { kubernetesTranslationRef } from '@backstage/plugin-kubernetes';

/**
 * Stock texts of the entity pages in pt-BR (Story 2.2, AC-14): the catalog
 * warnings above the overview (orphan, missing relations, processing
 * errors), the stock cards still shown on some kinds (Sobre, Links, Labels…)
 * and the search result item. The keys the Catálogo already overrides
 * (`catalogTable.*`, `entityContextMenu.*`, in `catalog/translations.ts`) are
 * not repeated here.
 */
export const catalogEntityMessages = TranslationBlueprint.make({
  name: 'byx-catalog-entity',
  params: {
    resource: createTranslationMessages({
      ref: catalogTranslationRef,
      messages: {
        'indexPage.title': 'Catálogo de {{orgName}}',
        'indexPage.createButtonTitle': 'Criar',
        'indexPage.supportButtonContent':
          'Todas as entidades do catálogo de software',
        'entityPage.notFoundMessage': 'Não há {{kind}} com {{link}} pedidos.',
        'entityPage.notFoundLinkText': 'tipo, namespace e nome',
        'aboutCard.title': 'Sobre',
        'aboutCard.refreshButtonTitle': 'Agendar atualização da entidade',
        'aboutCard.editButtonTitle': 'Editar metadados',
        'aboutCard.editButtonAriaLabel': 'Editar',
        'aboutCard.createSimilarButtonTitle': 'Criar algo parecido',
        'aboutCard.refreshScheduledMessage': 'Atualização agendada.',
        'aboutCard.refreshButtonAriaLabel': 'Atualizar',
        'aboutCard.launchTemplate': 'Usar template',
        'aboutCard.viewTechdocs': 'Ver TechDocs',
        'aboutCard.viewSource': 'Ver fonte',
        'aboutCard.unknown': 'desconhecido',
        'aboutCard.descriptionField.label': 'Descrição',
        'aboutCard.descriptionField.value': 'Sem descrição',
        'aboutCard.ownerField.label': 'Dono',
        'aboutCard.ownerField.value': 'Sem dono',
        'aboutCard.domainField.label': 'Domínio',
        'aboutCard.domainField.value': 'Sem domínio',
        'aboutCard.systemField.label': 'Sistema',
        'aboutCard.systemField.value': 'Sem sistema',
        'aboutCard.parentComponentField.label': 'Componente pai',
        'aboutCard.parentComponentField.value': 'Sem componente pai',
        'aboutCard.kindField.label': 'Tipo de entidade',
        'aboutCard.typeField.label': 'Tipo',
        'aboutCard.lifecycleField.label': 'Ciclo de vida',
        'aboutCard.tagsField.label': 'Tags',
        'aboutCard.tagsField.value': 'Sem tags',
        'aboutCard.targetsField.label': 'Alvos',
        'searchResultItem.kind': 'Tipo de entidade',
        'searchResultItem.type': 'Tipo',
        'searchResultItem.lifecycle': 'Ciclo de vida',
        'searchResultItem.owner': 'Dono',
        'catalogTable.warningPanelTitle':
          'Não foi possível carregar as entidades do catálogo.',
        'catalogTable.allFilters': 'Todos',
        'dependencyOfComponentsCard.title': 'É dependência de',
        'dependencyOfComponentsCard.emptyMessage':
          'Nenhum componente depende deste componente.',
        'dependsOnComponentsCard.title': 'Depende de componentes',
        'dependsOnComponentsCard.emptyMessage':
          'Nenhum componente é dependência deste componente.',
        'dependsOnResourcesCard.title': 'Depende de recursos',
        'dependsOnResourcesCard.emptyMessage':
          'Nenhum recurso é dependência deste componente.',
        'entityLabelsCard.title': 'Rótulos',
        'entityLabelsCard.columnKeyLabel': 'Rótulo',
        'entityLabelsCard.columnValueLabel': 'Valor',
        'entityLabelsCard.emptyDescription':
          'Nenhum rótulo definido para esta entidade. Você pode adicionar rótulos ao YAML da entidade como no exemplo destacado abaixo:',
        'entityLabelsCard.readMoreButtonTitle': 'Saiba mais',
        'entityLabels.warningPanelTitle': 'Entidade não encontrada',
        'entityLabels.ownerLabel': 'Dono',
        'entityLabels.lifecycleLabel': 'Ciclo de vida',
        'entityLabels.systemLabel': 'Sistema',
        'entityLabels.domainLabel': 'Domínio',
        'entityLabels.partOfLabel': 'Parte de',
        'entityLinksCard.title': 'Links',
        'entityLinksCard.emptyDescription':
          'Nenhum link definido para esta entidade. Você pode adicionar links ao YAML da entidade como no exemplo destacado abaixo:',
        'entityLinksCard.readMoreButtonTitle': 'Saiba mais',
        'entityNotFound.title': 'Entidade não encontrada',
        'entityNotFound.description':
          'Quer ajudar a construir isto? Veja a documentação de primeiros passos.',
        'entityNotFound.docButtonTitle': 'Documentação',
        'entityTabs.tabsAriaLabel': 'Abas',
        'deleteEntity.dialogTitle':
          'Tem certeza de que quer excluir esta entidade?',
        'deleteEntity.deleteButtonTitle': 'Excluir',
        'deleteEntity.cancelButtonTitle': 'Cancelar',
        'deleteEntity.description':
          'Esta entidade não é referenciada por nenhuma localização e por isso não recebe atualizações.',
        'deleteEntity.actionButtonTitle': 'Excluir entidade',
        'catalogExportButton.triggerButtonTitle': 'Exportar seleção',
        'catalogExportButton.dialogTitle': 'Exportar seleção do catálogo',
        'catalogExportButton.formatLabel': 'Formato',
        'catalogExportButton.columnsLabel': 'Colunas',
        'catalogExportButton.cancelButtonTitle': 'Cancelar',
        'catalogExportButton.confirmButtonTitle': 'Confirmar',
        'catalogExportButton.exportingButtonTitle': 'Exportando…',
        'catalogExportButton.successMessage': 'Catálogo exportado.',
        'catalogExportButton.errorMessage':
          'Não foi possível exportar o catálogo: {{errorMessage}}',
        entityProcessingErrorsDescription: 'O erro abaixo vem de',
        // U3F-3: the warning above the overview of an entity whose relations
        // point at entities missing from the catalog. The list follows it.
        entityRelationWarningDescription:
          'Esta entidade tem relações com entidades que não estão no catálogo.\n Entidades não encontradas: ',
        'hasComponentsCard.title': 'Componentes',
        'hasComponentsCard.emptyMessage':
          'Nenhum componente faz parte deste sistema.',
        'hasResourcesCard.title': 'Recursos',
        'hasResourcesCard.emptyMessage':
          'Nenhum recurso faz parte deste sistema.',
        'hasSubcomponentsCard.title': 'Subcomponentes',
        'hasSubcomponentsCard.emptyMessage':
          'Nenhum subcomponente faz parte deste componente.',
        'hasSubdomainsCard.title': 'Subdomínios',
        'hasSubdomainsCard.emptyMessage':
          'Nenhum subdomínio faz parte deste domínio.',
        'hasSystemsCard.title': 'Sistemas',
        'hasSystemsCard.emptyMessage':
          'Nenhum sistema faz parte deste domínio.',
        'relatedEntitiesCard.emptyHelpLinkTitle': 'Saiba como mudar isso.',
        'systemDiagramCard.title': 'Diagrama do sistema',
        'systemDiagramCard.description':
          'Use o gesto de pinça e o zoom para navegar pelo diagrama.',
        'systemDiagramCard.edgeLabels.partOf': 'parte de',
        'systemDiagramCard.edgeLabels.provides': 'fornece',
        'systemDiagramCard.edgeLabels.dependsOn': 'depende de',
      },
    }),
  },
});

/**
 * Catalog-react texts of the entity pages that the Catálogo filters do not
 * cover: the ⋯ menu dialogs (Inspecionar, Remover), the missing-annotation
 * empty state of tabs and the column titles of the stock relation tables.
 */
export const catalogReactEntityMessages = TranslationBlueprint.make({
  name: 'byx-catalog-react-entity',
  params: {
    resource: createTranslationMessages({
      ref: catalogReactTranslationRef,
      messages: {
        'entityKindPicker.title': 'Tipo de entidade',
        'entityKindPicker.errorMessage':
          'Não foi possível carregar os tipos de entidade.',
        'entityNamespacePicker.title': 'Namespace',
        'entityProcessingStatusPicker.title': 'Status de processamento',
        'entityTagPicker.title': 'Tags',
        'entityPeekAheadPopover.title':
          'Abra a entidade para ver todas as tags.',
        'entityPeekAheadPopover.entityCardActionsAriaLabel': 'Mostrar',
        'entityPeekAheadPopover.entityCardActionsTitle': 'Ver detalhes',
        'entityPeekAheadPopover.emailCardAction.title':
          'Enviar e-mail para {{email}}',
        'entityPeekAheadPopover.emailCardAction.ariaLabel': 'E-mail',
        'entityPeekAheadPopover.emailCardAction.subTitle': 'mailto {{email}}',
        'entityTypePicker.errorMessage': 'Não foi possível carregar os tipos.',
        'inspectEntityDialog.title': 'Inspecionar entidade',
        'inspectEntityDialog.closeButtonTitle': 'Fechar',
        'inspectEntityDialog.tabsAriaLabel': 'Opções de inspeção',
        'inspectEntityDialog.ancestryPage.title': 'Origem',
        'inspectEntityDialog.ancestryPage.description':
          'A cadeia de entidades acima desta: as entidades das quais {{processorsLink}} filhas que levaram à existência desta. É um mecanismo diferente das relações.',
        'inspectEntityDialog.ancestryPage.processorsLink':
          'os processadores emitiram',
        'inspectEntityDialog.colocatedPage.title': 'Mesma origem',
        'inspectEntityDialog.colocatedPage.description':
          'Entidades que vêm da mesma fonte de dados que esta (por exemplo, o mesmo arquivo YAML) ou da mesma origem (por exemplo, a URL registrada originalmente).',
        'inspectEntityDialog.colocatedPage.alertNoLocation':
          'A entidade não tem informação de localização.',
        'inspectEntityDialog.colocatedPage.alertNoEntity':
          'Não há outras entidades nesta localização.',
        'inspectEntityDialog.colocatedPage.locationHeader':
          'Na mesma localização',
        'inspectEntityDialog.colocatedPage.originHeader': 'Na mesma origem',
        'inspectEntityDialog.colocatedPage.entityListAriaLabel':
          'Entidades da mesma origem',
        'inspectEntityDialog.jsonPage.title': 'Entidade em JSON',
        'inspectEntityDialog.jsonPage.description':
          'Os dados brutos da entidade, como vêm do catálogo, em JSON.',
        'inspectEntityDialog.overviewPage.title': 'Visão geral',
        'inspectEntityDialog.overviewPage.metadata.title': 'Metadados',
        'inspectEntityDialog.overviewPage.labels': 'Rótulos',
        'inspectEntityDialog.overviewPage.status.title': 'Status',
        'inspectEntityDialog.overviewPage.identity.title': 'Identidade',
        'inspectEntityDialog.overviewPage.tags': 'Tags',
        'inspectEntityDialog.overviewPage.annotations': 'Anotações',
        'inspectEntityDialog.overviewPage.relation.title': 'Relações',
        'inspectEntityDialog.overviewPage.copyAriaLabel': 'Copiar {{label}}',
        'inspectEntityDialog.overviewPage.copiedStatus': 'Copiado',
        'inspectEntityDialog.overviewPage.helpLinkAriaLabel': 'Saiba mais',
        'inspectEntityDialog.yamlPage.title': 'Entidade em YAML',
        'inspectEntityDialog.yamlPage.description':
          'Os dados brutos da entidade, como vêm do catálogo, em YAML.',
        'inspectEntityDialog.tabNames.json': 'JSON bruto',
        'inspectEntityDialog.tabNames.yaml': 'YAML bruto',
        'inspectEntityDialog.tabNames.overview': 'Visão geral',
        'inspectEntityDialog.tabNames.ancestry': 'Origem',
        'inspectEntityDialog.tabNames.colocated': 'Mesma origem',
        'unregisterEntityDialog.title':
          'Tem certeza de que quer remover esta entidade do catálogo?',
        'unregisterEntityDialog.cancelButtonTitle': 'Cancelar',
        'unregisterEntityDialog.deleteButtonTitle': 'Excluir entidade',
        'unregisterEntityDialog.deleteEntitySuccessMessage':
          'Entidade {{entityName}} removida.',
        'unregisterEntityDialog.onlyDeleteStateTitle':
          'Esta entidade não parece vir de uma localização registrada. Por isso, a única opção é excluí-la do catálogo.',
        'unregisterEntityDialog.errorStateTitle':
          'Erro interno: estado desconhecido',
        'unregisterEntityDialog.bootstrapState.title':
          'Não é possível remover esta entidade: ela vem de uma configuração protegida do Backstage (localização "{{location}}"). Se isso parecer um erro, fale com o integrador do {{appTitle}}.',
        'unregisterEntityDialog.bootstrapState.advancedDescription':
          'Você pode excluir a própria entidade do catálogo. Faça isso só se souber que o arquivo do catálogo foi apagado ou movido da localização de origem; caso contrário, a entidade volta na próxima atualização do catálogo.',
        'unregisterEntityDialog.bootstrapState.advancedOptions':
          'Opções avançadas',
        'unregisterEntityDialog.unregisterState.title':
          'Esta ação remove do catálogo as seguintes entidades:',
        'unregisterEntityDialog.unregisterState.description':
          'Para desfazer, registre a entidade de novo no {{appTitle}}.',
        'unregisterEntityDialog.unregisterState.subTitle': 'Localizadas em:',
        'unregisterEntityDialog.unregisterState.advancedDescription':
          'Você também pode excluir a própria entidade do catálogo. Faça isso só se souber que o arquivo do catálogo foi apagado ou movido da localização de origem; caso contrário, a entidade volta na próxima atualização do catálogo.',
        'unregisterEntityDialog.unregisterState.advancedOptions':
          'Opções avançadas',
        'unregisterEntityDialog.unregisterState.unregisterButtonTitle':
          'Remover localização',
        'userListPicker.defaultOrgName': 'Empresa',
        'entityTableColumnTitle.label': 'Rótulo',
        'entityTableColumnTitle.namespace': 'Namespace',
        'entityTableColumnTitle.domain': 'Domínio',
        'entityTableColumnTitle.targets': 'Alvos',
        'entityRelationCard.emptyHelpLinkTitle': 'Saiba como mudar isso.',
        'missingAnnotationEmptyState.title': 'Anotação ausente',
        'missingAnnotationEmptyState.readMore': 'Saiba mais',
        'missingAnnotationEmptyState.annotationYaml':
          'Adicione a anotação ao YAML do seu {{entityKind}} como no exemplo destacado abaixo:',
        'missingAnnotationEmptyState.generateDescription_one':
          'Falta a anotação {{annotations}}. Adicione-a ao seu {{entityKind}} para ativar esta ferramenta.',
        'missingAnnotationEmptyState.generateDescription_other':
          'Faltam as anotações {{annotations}}. Adicione-as ao seu {{entityKind}} para ativar esta ferramenta.',
      },
    }),
  },
});

/** Every API Docs text: the APIs card (`has-apis`), definitions, dialogs. */
export const apiDocsMessages = TranslationBlueprint.make({
  name: 'byx-api-docs',
  params: {
    resource: createTranslationMessages({
      ref: apiDocsTranslationRef,
      messages: {
        'apiDefinitionCard.error.title': 'Não foi possível carregar a API',
        'apiDefinitionCard.rawButtonTitle': 'Bruto',
        'apiDefinitionDialog.closeButtonTitle': 'Fechar',
        'apiDefinitionDialog.tabsAriaLabel': 'Opções da definição da API',
        'apiDefinitionDialog.toggleButtonAriaLabel':
          'Abrir ou fechar a definição da API',
        'apiDefinitionDialog.rawButtonTitle': 'Bruto',
        'defaultApiExplorerPage.title': 'APIs',
        'defaultApiExplorerPage.subtitle': 'Explorador de APIs de {{orgName}}',
        'defaultApiExplorerPage.pageTitleOverride': 'APIs',
        'defaultApiExplorerPage.createButtonTitle': 'Registrar API existente',
        'defaultApiExplorerPage.supportButtonTitle': 'Todas as suas APIs',
        'consumedApisCard.title': 'APIs consumidas',
        'consumedApisCard.error.title': 'Não foi possível carregar as APIs',
        'consumedApisCard.emptyContent.title':
          'Este {{entity}} não consome nenhuma API.',
        'hasApisCard.title': 'APIs',
        'hasApisCard.error.title': 'Não foi possível carregar as APIs',
        'hasApisCard.emptyContent.title':
          'Este {{entity}} não contém nenhuma API.',
        'providedApisCard.title': 'APIs fornecidas',
        'providedApisCard.error.title': 'Não foi possível carregar as APIs',
        'providedApisCard.emptyContent.title':
          'Este {{entity}} não fornece nenhuma API.',
        'apiEntityColumns.typeTitle': 'Tipo',
        'apiEntityColumns.apiDefinitionTitle': 'Definição da API',
        'consumingComponentsCard.title': 'Consumidores',
        'consumingComponentsCard.error.title':
          'Não foi possível carregar os componentes',
        'consumingComponentsCard.emptyContent.title':
          'Nenhum componente consome esta API.',
        'providingComponentsCard.title': 'Fornecedores',
        'providingComponentsCard.error.title':
          'Não foi possível carregar os componentes',
        'providingComponentsCard.emptyContent.title':
          'Nenhum componente fornece esta API.',
        apisCardHelpLinkTitle: 'Saiba como mudar isso.',
      },
    }),
  },
});

/**
 * Core-components texts the entity pages can show besides the table chrome
 * (already in `catalog/translations.ts`): the missing-annotation empty
 * state, the error page and boundary, copy and log buttons. The labels inside
 * `ErrorPanel` ("Error", "Message") are hard-coded in core-components 0.18
 * and have no translation key.
 */
export const coreComponentsMessages = TranslationBlueprint.make({
  name: 'byx-core-components',
  params: {
    resource: createTranslationMessages({
      ref: coreComponentsTranslationRef,
      messages: {
        'table.filter.title': 'Filtros',
        'table.filter.placeholder': 'Todos os resultados',
        'table.filter.clearAll': 'Limpar tudo',
        'emptyState.missingAnnotation.title': 'Anotação ausente',
        'emptyState.missingAnnotation.actionTitle':
          'Adicione a anotação ao YAML do componente como no exemplo destacado abaixo:',
        'emptyState.missingAnnotation.readMore': 'Saiba mais',
        skipToContent: 'Pular para o conteúdo',
        'copyTextButton.tooltipText': 'Texto copiado.',
        'errorPage.title': 'Algo deu errado.',
        'errorPage.subtitle': 'ERRO {{status}}: {{statusMessage}}',
        'errorPage.goBack': 'Voltar',
        'errorPage.showMoreDetails': 'Mostrar mais detalhes',
        'errorPage.showLessDetails': 'Mostrar menos detalhes',
        'errorBoundary.title': 'Fale com {{slackChannel}} para pedir ajuda.',
        'supportButton.title': 'Suporte',
        'supportButton.close': 'Fechar',
        'alertDisplay.message_one': '({{ count }} mensagem mais nova)',
        'alertDisplay.message_other': '({{ count }} mensagens mais novas)',
        'dependencyGraph.fullscreenTooltip': 'Alternar tela cheia',
        'logViewer.searchField.placeholder': 'Buscar',
        'logViewer.downloadBtn.tooltip': 'Baixar logs',
        'logViewer.copyBtn.tooltip': 'Copiar logs',
      },
    }),
  },
});

/** Every Argo CD text (Deploys tab: Ciclo de deploy, Resumo de deploy). */
export const argocdMessages = TranslationBlueprint.make({
  name: 'byx-argocd',
  params: {
    resource: createTranslationMessages({
      ref: argocdTranslationRef,
      messages: {
        'appStatus.appHealthStatus.Healthy': 'Saudável',
        'appStatus.appHealthStatus.Suspended': 'Suspenso',
        'appStatus.appHealthStatus.Degraded': 'Degradado',
        'appStatus.appHealthStatus.Progressing': 'Em andamento',
        'appStatus.appHealthStatus.Missing': 'Ausente',
        'appStatus.appHealthStatus.Unknown': 'Desconhecido',
        'appStatus.appSyncStatus.Unknown': 'Desconhecido',
        'appStatus.appSyncStatus.Synced': 'Sincronizado',
        'appStatus.appSyncStatus.OutOfSync': 'Fora de sincronia',
        'common.appServer.title':
          'Este é o cluster local onde o Argo CD está instalado.',
        'common.permissionAlert.alertTitle': 'Permissão necessária',
        'common.permissionAlert.alertText':
          'Para ver o Argo CD, peça ao administrador a permissão argocd.view.read.',
        'deploymentLifecycle.sidebar.resources.resourcesColumnHeader.name':
          'Nome',
        'deploymentLifecycle.sidebar.resources.resourcesColumnHeader.kind':
          'Tipo',
        'deploymentLifecycle.sidebar.resources.resourcesColumnHeader.createdAt':
          'Criado em',
        'deploymentLifecycle.sidebar.resources.resourcesColumnHeader.syncStatus':
          'Sincronização',
        'deploymentLifecycle.sidebar.resources.resourcesColumnHeader.healthStatus':
          'Saúde',
        'deploymentLifecycle.sidebar.resources.resourcesTable.ariaLabelledBy':
          'Recursos',
        'deploymentLifecycle.sidebar.resources.resourcesTable.noneFound':
          'Nenhum recurso encontrado',
        'deploymentLifecycle.sidebar.resources.resourcesTableRow.ariaLabel':
          'expandir linha',
        'deploymentLifecycle.sidebar.resources.resource.deploymentHistory.bodyText':
          'Histórico de deploys',
        'deploymentLifecycle.sidebar.resources.resource.deploymentHistoryCommit.deployedText':
          'implantado',
        'deploymentLifecycle.sidebar.resources.resource.deploymentMetadata.metadataItemWithTooltip.title':
          'Imagens',
        'deploymentLifecycle.sidebar.resources.resource.deploymentMetadata.metadataItemWithTooltip.tooltipText':
          'As imagens de todos os deployments da aplicação no Argo CD.',
        'deploymentLifecycle.sidebar.resources.resource.deploymentMetadata.namespace':
          'Namespace',
        'deploymentLifecycle.sidebar.resources.resource.deploymentMetadata.commit':
          'Commit',
        'deploymentLifecycle.sidebar.resources.resource.rolloutMetadata.namespace':
          'Namespace',
        'deploymentLifecycle.sidebar.resources.resource.rolloutMetadata.strategy':
          'Estratégia',
        'deploymentLifecycle.sidebar.resources.resource.rolloutMetadata.status':
          'Status',
        'deploymentLifecycle.sidebar.resources.resource.resourceMetadata.namespace':
          'Namespace',
        'deploymentLifecycle.sidebar.resources.resourcesKebabMenuOptions.iconButton.ariaLabel':
          'mais',
        'deploymentLifecycle.sidebar.resources.resourcesKebabMenuOptions.refresh':
          'Atualizar',
        'deploymentLifecycle.sidebar.resources.resourcesKebabMenuOptions.sync':
          'Sincronizar',
        'deploymentLifecycle.sidebar.resources.resourcesSearchBar.placeholder':
          'Buscar por tipo',
        'deploymentLifecycle.sidebar.resources.resourcesSearchBar.ariaLabel':
          'limpar busca',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.SearchByName':
          'Nome',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.Kind':
          'Tipo',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.SyncStatus':
          'Sincronização',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.HealthStatus':
          'Saúde',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.Unset':
          'Filtrar por',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.searchByNameInput':
          'Buscar por nome',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusInput':
          'Filtrar por saúde',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.syncStatusInput':
          'Filtrar por sincronização',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.kindInput':
          'Filtrar por tipo',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.ariaLabels.resourceFilters':
          'Filtros de recursos',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.ariaLabels.syncStatus':
          'Sincronização',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.ariaLabels.kind':
          'Tipo',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusSelectOptions.Healthy':
          'Saudável',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusSelectOptions.Suspended':
          'Suspenso',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusSelectOptions.Degraded':
          'Degradado',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusSelectOptions.Progressing':
          'Em andamento',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusSelectOptions.Missing':
          'Ausente',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.healthStatusSelectOptions.Unknown':
          'Desconhecido',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.syncStatusSelectOptions.Synced':
          'Sincronizado',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.syncStatusSelectOptions.Unknown':
          'Desconhecido',
        'deploymentLifecycle.sidebar.resources.filters.resourcesFilterBy.syncStatusSelectOptions.OutOfSync':
          'Fora de sincronia',
        'deploymentLifecycle.sidebar.rollouts.revisions.analysisRuns.analysisRuns.textPrimary':
          'Execuções de análise',
        'deploymentLifecycle.sidebar.rollouts.revisions.analysisRuns.analysisRuns.name':
          'Nome:',
        'deploymentLifecycle.sidebar.rollouts.revisions.analysisRuns.analysisRuns.createdAt':
          'Criado em:',
        'deploymentLifecycle.sidebar.rollouts.revisions.analysisRuns.analysisRuns.status':
          'Status:',
        'deploymentLifecycle.sidebar.rollouts.revisions.analysisRuns.analysisRuns.chipLabel':
          'Análise',
        'deploymentLifecycle.sidebar.rollouts.revisions.blueGreenRevision.revision':
          'Revisão',
        'deploymentLifecycle.sidebar.rollouts.revisions.blueGreenRevision.stable':
          'Estável',
        'deploymentLifecycle.sidebar.rollouts.revisions.blueGreenRevision.active':
          'Ativa',
        'deploymentLifecycle.sidebar.rollouts.revisions.blueGreenRevision.preview':
          'Prévia',
        'deploymentLifecycle.sidebar.rollouts.revisions.canaryRevision.revision':
          'Revisão',
        'deploymentLifecycle.sidebar.rollouts.revisions.canaryRevision.revisionType.stable':
          'Estável',
        'deploymentLifecycle.sidebar.rollouts.revisions.canaryRevision.revisionType.canary':
          'Canary',
        'deploymentLifecycle.sidebar.rollouts.revisions.revisionImage.textPrimary':
          'Tráfego para a imagem',
        'deploymentLifecycle.sidebar.rollouts.rollOut.title': 'Revisões',
        'deploymentLifecycle.deploymentLifecycle.title': 'Ciclo de deploy',
        'deploymentLifecycle.deploymentLifecycle.subtitle':
          'Componentes e sistemas implantados no namespace, segundo o Argo CD',
        'deploymentLifecycle.deploymentLifecycleHeader.openInArgoCD':
          'Abrir {{appName}} no Argo CD',
        'deploymentLifecycle.deploymentLifecycleCard.instance': 'Instância',
        'deploymentLifecycle.deploymentLifecycleCard.server': 'Servidor',
        'deploymentLifecycle.deploymentLifecycleCard.namespace': 'Namespace',
        'deploymentLifecycle.deploymentLifecycleCard.commit': 'Commit',
        'deploymentLifecycle.deploymentLifecycleCard.tooltipText':
          'O SHA abaixo é o último commit da primeira fonte definida na Application.',
        'deploymentLifecycle.deploymentLifecycleCard.resources': 'Recursos',
        'deploymentLifecycle.deploymentLifecycleCard.resourcesDeployed':
          'recursos implantados',
        'deploymentLifecycle.deploymentLifecycleDrawer.iconButtonTitle':
          'Fechar o painel',
        'deploymentLifecycle.deploymentLifecycleDrawer.instance': 'Instância',
        'deploymentLifecycle.deploymentLifecycleDrawer.cluster': 'Cluster',
        'deploymentLifecycle.deploymentLifecycleDrawer.namespace': 'Namespace',
        'deploymentLifecycle.deploymentLifecycleDrawer.commit': 'Commit',
        'deploymentLifecycle.deploymentLifecycleDrawer.revision': 'Revisão',
        'deploymentLifecycle.deploymentLifecycleDrawer.resources': 'Recursos',
        'deploymentLifecycle.deploymentLifecycleDrawer.instanceDefaultValue':
          'padrão',
        'deploymentSummary.deploymentSummary.tableTitle': 'Resumo de deploy',
        'deploymentSummary.deploymentSummary.columns.application': 'Aplicação',
        'deploymentSummary.deploymentSummary.columns.namespace': 'Namespace',
        'deploymentSummary.deploymentSummary.columns.instance': 'Instância',
        'deploymentSummary.deploymentSummary.columns.server': 'Servidor',
        'deploymentSummary.deploymentSummary.columns.revision': 'Revisão',
        'deploymentSummary.deploymentSummary.columns.lastDeployed':
          'Último deploy',
        'deploymentSummary.deploymentSummary.columns.syncStatus':
          'Sincronização',
        'deploymentSummary.deploymentSummary.columns.healthStatus': 'Saúde',
      },
    }),
  },
});

/**
 * Every Kubernetes plugin text (Deploys tab). The resource tables inside it
 * come from `@backstage/plugin-kubernetes-react`, which is not an app
 * dependency and stays in English (known gap, AC-14).
 */
export const kubernetesMessages = TranslationBlueprint.make({
  name: 'byx-kubernetes',
  params: {
    resource: createTranslationMessages({
      ref: kubernetesTranslationRef,
      messages: {
        'kubernetesContentPage.permissionAlert.title': 'Permissão necessária',
        'kubernetesContentPage.permissionAlert.message':
          "Para ver os objetos do Kubernetes, peça ao administrador do portal as permissões 'kubernetes.clusters.read' e 'kubernetes.resources.read'.",
        'kubernetesContentPage.title': 'Seus clusters',
        'kubernetesContentPage.emptyState.title':
          'Nenhum recurso no Kubernetes',
        'kubernetesContentPage.emptyState.description':
          'Nenhum recurso em nenhum cluster conhecido para {{entityName}}',
        'entityContent.title': 'Kubernetes',
      },
    }),
  },
});

/** The entity-page message overrides, in the order they are registered. */
export const entityTranslations = [
  catalogEntityMessages,
  catalogReactEntityMessages,
  apiDocsMessages,
  coreComponentsMessages,
  argocdMessages,
  kubernetesMessages,
];
